import Analytics from "@/components/Analytics";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

import Directory from "@/pages/Directory";
import Storefront from "@/pages/Storefront";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Legal from "@/pages/Legal";
import OnboardingInstagram from "@/pages/OnboardingInstagram";
import AdminLayout from "@/pages/admin/AdminLayout";
import Overview from "@/pages/admin/Overview";
import StoresPage from "@/pages/admin/Stores";
import ProductsPage from "@/pages/admin/Products";
import OrdersPage from "@/pages/admin/Orders";
import LeadsPage from "@/pages/admin/Leads";
import FrontpagePage from "@/pages/admin/Frontpage";
import LegalAdmin from "@/pages/admin/Legal";
import SettingsPage from "@/pages/admin/Settings";
import PopupsPage from "@/pages/admin/Popups";
import MailerPage from "@/pages/admin/Mailer";
import AnalyticsPage from "@/pages/admin/Analytics";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-black border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Directory />} />
      <Route path="/store/:handle" element={<StoreRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/legal/:slug" element={<Legal />} />
      <Route path="/onboarding/instagram" element={<OnboardingInstagram />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Overview />} />
        <Route path="stores" element={<StoresPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="frontpage" element={<FrontpagePage />} />
        <Route path="legal" element={<LegalAdmin />} />
        <Route path="popups" element={<PopupsPage />} />
        <Route path="mailer" element={<MailerPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="/:handle" element={<Storefront />} />
    </Routes>
  );
}

function StoreRedirect() {
  const { handle } = useParams();
  const location = useLocation();
  return <Navigate to={`/${handle}${location.search}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Analytics />
            <AppRoutes />
            <Toaster position="top-center" />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
